/**
 * Semantic Distance Visualization Component
 *
 * Dual SVG graphs showing how FAQ questions move closer to the product
 * as AEO elements are added. Uses CSS transitions for smooth animations.
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { FAQQuestion } from '@/services/aeoDemoService';

interface SemanticDistanceVizProps {
  questions: FAQQuestion[];
  hasEnhancement: boolean;
  fullSize?: boolean;
}

const CATEGORY_COLORS: Record<string, string> = {
  specification: '#3b82f6',
  comparison: '#f59e0b',
  use_case: '#10b981',
  value: '#8b5cf6',
  compatibility: '#ec4899',
  maintenance: '#06b6d4',
  purchase: '#ef4444',
  default: '#6b7280',
};

const VIEW_SIZE = 300;
const CENTER = VIEW_SIZE / 2;
const PRODUCT_RADIUS = 20;
const QUESTION_RADIUS = 8;
const MAX_DISTANCE = CENTER - QUESTION_RADIUS - 10;
const GHOST_COLOR = '#6b7280'; // Works in both light and dark mode

function QuestionNode({
  question,
  x,
  y,
  isGhost = false,
  onHover,
}: {
  question: FAQQuestion;
  x: number;
  y: number;
  isGhost?: boolean;
  onHover?: (question: FAQQuestion | null, pos: { x: number; y: number }) => void;
}) {
  const color = CATEGORY_COLORS[question.category] || CATEGORY_COLORS.default;

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!isGhost && onHover) {
      const rect = (e.currentTarget as SVGElement).ownerSVGElement?.getBoundingClientRect();
      if (rect) {
        onHover(question, { x: rect.left + x + 20, y: rect.top + y });
      }
    }
  };

  const handleMouseLeave = () => {
    if (!isGhost && onHover) {
      onHover(null, { x: 0, y: 0 });
    }
  };

  return (
    <g
      style={{
        transform: `translate(${x}px, ${y}px)`,
        transition: 'transform 0.5s ease-out',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Connection line to center */}
      <line
        x1={0}
        y1={0}
        x2={CENTER - x}
        y2={CENTER - y}
        stroke={isGhost ? GHOST_COLOR : color}
        strokeWidth={isGhost ? 1 : 1.5}
        strokeDasharray={isGhost ? '4,4' : undefined}
        opacity={isGhost ? 0.4 : 0.6}
      />

      {/* Question node */}
      <circle
        cx={0}
        cy={0}
        r={QUESTION_RADIUS}
        fill={isGhost ? 'transparent' : color}
        stroke={isGhost ? GHOST_COLOR : color}
        strokeWidth={isGhost ? 2 : 0}
        strokeDasharray={isGhost ? '4,4' : undefined}
        className={cn(!isGhost && 'cursor-pointer')}
        style={{ transition: 'opacity 0.15s ease' }}
      />

      {/* Question number */}
      {!isGhost && (
        <text
          x={0}
          y={0}
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontSize="10"
          fontWeight="bold"
          pointerEvents="none"
        >
          {question.id}
        </text>
      )}

      {/* Native SVG title for basic tooltip fallback */}
      {!isGhost && (
        <title>{question.text}</title>
      )}
    </g>
  );
}

function DistanceGraph({
  title,
  questions,
  showEnhanced,
  subtitle,
  fullSize = false,
}: {
  title: string;
  questions: FAQQuestion[];
  showEnhanced: boolean;
  subtitle?: string;
  fullSize?: boolean;
}) {
  const [hoveredQuestion, setHoveredQuestion] = useState<{
    question: FAQQuestion;
    pos: { x: number; y: number };
  } | null>(null);

  // Calculate positions based on 2D projections and distances
  const positions = useMemo(() => {
    return questions.map(q => {
      // Use position2d for angle, distance for radius
      const angle = Math.atan2(q.position2d.y, q.position2d.x);
      const originalRadius = Math.min(q.originalDistance, 1) * MAX_DISTANCE;
      const currentRadius = showEnhanced && q.currentDistance !== undefined
        ? Math.min(q.currentDistance, 1) * MAX_DISTANCE
        : originalRadius;

      return {
        question: q,
        originalX: CENTER + Math.cos(angle) * originalRadius,
        originalY: CENTER + Math.sin(angle) * originalRadius,
        currentX: CENTER + Math.cos(angle) * currentRadius,
        currentY: CENTER + Math.sin(angle) * currentRadius,
      };
    });
  }, [questions, showEnhanced]);

  const handleHover = (question: FAQQuestion | null, pos: { x: number; y: number }) => {
    if (question) {
      setHoveredQuestion({ question, pos });
    } else {
      setHoveredQuestion(null);
    }
  };

  return (
    <div className="relative">
      <div className="text-center mb-2">
        <h4 className="text-sm font-medium">{title}</h4>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>

      <svg
        viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`}
        className={cn(
          "w-full border rounded-lg bg-muted/10",
          fullSize ? "h-[400px]" : "h-64"
        )}
      >
        {/* Distance rings */}
        {[0.25, 0.5, 0.75, 1].map((ratio, i) => (
          <circle
            key={i}
            cx={CENTER}
            cy={CENTER}
            r={ratio * MAX_DISTANCE}
            fill="none"
            className="stroke-muted-foreground/30"
            strokeWidth={1}
            strokeDasharray="4,4"
          />
        ))}

        {/* Ghost positions (original locations in enhanced view) */}
        {showEnhanced && positions.map(({ question, originalX, originalY }) => (
          <QuestionNode
            key={`ghost-${question.id}`}
            question={question}
            x={originalX}
            y={originalY}
            isGhost
          />
        ))}

        {/* Current question positions */}
        {positions.map(({ question, originalX, originalY, currentX, currentY }) => (
          <QuestionNode
            key={question.id}
            question={question}
            x={showEnhanced ? currentX : originalX}
            y={showEnhanced ? currentY : originalY}
            onHover={handleHover}
          />
        ))}

        {/* Product node (center) */}
        <g>
          <circle
            cx={CENTER}
            cy={CENTER}
            r={PRODUCT_RADIUS}
            fill="#3b82f6"
            className="drop-shadow-md"
          />
          <text
            x={CENTER}
            y={CENTER}
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize="10"
            fontWeight="bold"
          >
            PAGE
          </text>
        </g>
      </svg>

      {/* Custom tooltip - rendered outside SVG */}
      {hoveredQuestion && (
        <div
          className="fixed z-50 px-3 py-2 text-sm bg-popover text-popover-foreground border rounded-lg shadow-lg max-w-xs pointer-events-none"
          style={{
            left: hoveredQuestion.pos.x,
            top: hoveredQuestion.pos.y,
            transform: 'translateY(-50%)',
          }}
        >
          <p className="font-medium">{hoveredQuestion.question.text}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Category: {hoveredQuestion.question.category.replace('_', ' ')}
          </p>
          <p className="text-xs mt-1">
            Relevance: {((1 - hoveredQuestion.question.originalDistance) * 100).toFixed(1)}%
            {hoveredQuestion.question.currentDistance !== undefined &&
              hoveredQuestion.question.currentDistance !== hoveredQuestion.question.originalDistance && (
              <span className="text-green-600 ml-1">
                → {((1 - hoveredQuestion.question.currentDistance) * 100).toFixed(1)}%
              </span>
            )}
          </p>
        </div>
      )}

      {/* Legend */}
      <div className="mt-2 flex flex-wrap gap-2 justify-center">
        {Object.entries(CATEGORY_COLORS)
          .filter(([key]) => key !== 'default')
          .slice(0, 5)
          .map(([key, color]) => (
            <div key={key} className="flex items-center gap-1 text-xs">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-muted-foreground capitalize">
                {key.replace('_', ' ')}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

export function SemanticDistanceViz({
  questions,
  hasEnhancement,
  fullSize = false,
}: SemanticDistanceVizProps) {
  // Calculate improvement summary
  const improvementSummary = useMemo(() => {
    if (!hasEnhancement) return null;

    const improved = questions.filter(
      q => q.currentDistance !== undefined && q.currentDistance < q.originalDistance
    );
    const avgImprovement =
      improved.length > 0
        ? improved.reduce((sum, q) => {
            const pct =
              ((q.originalDistance - q.currentDistance!) / q.originalDistance) * 100;
            return sum + pct;
          }, 0) / improved.length
        : 0;

    return {
      improvedCount: improved.length,
      totalCount: questions.length,
      avgImprovement: avgImprovement.toFixed(1),
    };
  }, [questions, hasEnhancement]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Semantic Relevance to Questions</CardTitle>
          {improvementSummary && (
            <div className="text-sm text-muted-foreground">
              <span className="text-green-600 font-medium">
                {improvementSummary.improvedCount}/{improvementSummary.totalCount}
              </span>
              {' '}questions more relevant (avg +{improvementSummary.avgImprovement}%)
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className={cn(
          "grid gap-6",
          fullSize ? "md:grid-cols-2" : "md:grid-cols-2"
        )}>
          <DistanceGraph
            title="Original Page"
            subtitle="Baseline semantic relevance"
            questions={questions}
            showEnhanced={false}
            fullSize={fullSize}
          />
          <DistanceGraph
            title="Enhanced Page"
            subtitle={hasEnhancement ? "With selected AEO elements" : "Select elements to see impact"}
            questions={questions}
            showEnhanced={hasEnhancement}
            fullSize={fullSize}
          />
        </div>

        <div className="mt-4 text-center text-xs text-muted-foreground">
          Questions closer to center = higher relevance = better AI answer potential
        </div>
      </CardContent>
    </Card>
  );
}

export default SemanticDistanceViz;
